import { Skeleton, Tabs, TabsList, TabsTrigger, TOUCH } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { ApprovalsPanel } from "@/components/pto/ApprovalsPanel";
import { BalanceCards } from "@/components/pto/BalanceCards";
import { MyRequestsTable } from "@/components/pto/MyRequestsTable";
import { PtoHeader } from "@/components/pto/PtoHeader";
import { RequestDialog } from "@/components/pto/RequestDialog";
import { PtoBanner } from "@/components/team/PtoBanner";
import { useCurrentPerson } from "@/hooks/use-current-person";
import { useBalances, useMyRequests, usePendingCounts } from "@/hooks/use-pto";
import {
  PTO_PAGE_TAB_LABELS,
  PTO_PAGE_TABS,
  type PtoEmailAction,
  type PtoPageTab,
} from "@/lib/enums";
import { ptoTab, showApprovalsTab } from "@/lib/pto/alerts";
import { daysOff } from "@/lib/pto/balances";
import type { MyRequest } from "@/lib/pto/my-requests";
import { todayInZone } from "@/lib/time/dates";

interface PtoSearch {
  year?: number;
  tab?: PtoPageTab;
  request?: "new";
  decide?: string;
  action?: PtoEmailAction;
}

/**
 * The PTO page (spec 1): balances and My requests, plus an Approvals tab for managers and the HR
 * approver. `?request=new` opens the request dialog (Home's PTO request button);
 * `?tab=approvals&decide=<id>&action=approve|reject` (an approver's email) opens that step.
 */
/** The element the My time off / Approvals tabs control (the tabs have no TabsContent). */
const VIEW_ID = "pto-view";

export function PtoScreen() {
  const search = useSearch({ from: "/dashboard/pto" });
  const navigate = useNavigate();
  const me = useCurrentPerson().data;
  const currentYear = Number(todayInZone(new Date()).slice(0, 4));
  const year = search.year ?? currentYear;
  const balances = useBalances(year);
  const requests = useMyRequests(year);
  const counts = usePendingCounts();
  const canApprove = showApprovalsTab(me, counts.data);
  // "loading": ?tab=approvals for someone not yet known to approve (a pure HR approver's
  // counts are still on their way); hold the body instead of flashing My time off.
  const tab = ptoTab(search.tab, me, counts.data, counts.isPending);
  const [editing, setEditing] = useState<MyRequest | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const open = dialogOpen || search.request === "new";
  const toDecide = (counts.data?.team ?? 0) + (counts.data?.hr ?? 0);

  const setSearch = (next: PtoSearch) =>
    void navigate({
      to: "/dashboard/pto",
      search: { year: search.year, tab: search.tab, ...next },
    });

  function openDialog(r: MyRequest | null) {
    setEditing(r);
    setDialogOpen(true);
  }

  function onOpenChange(next: boolean) {
    setDialogOpen(next);
    if (!next) {
      setEditing(null);
      if (search.request) setSearch({ request: undefined });
    }
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <PtoHeader
        year={year}
        currentYear={currentYear}
        onYearChange={(y) => setSearch({ year: y === currentYear ? undefined : y })}
        daysOff={balances.data ? daysOff(balances.data.rows) : null}
        onRequest={() => openDialog(null)}
      />

      {tab === "mine" && (
        <PtoBanner count={toDecide} onReview={() => setSearch({ tab: "approvals" })} />
      )}

      {canApprove && (
        <Tabs
          value={tab}
          onValueChange={(v) => setSearch({ tab: PTO_PAGE_TABS.find((t) => t === v) ?? "mine" })}
        >
          <TabsList aria-label="PTO view">
            {PTO_PAGE_TABS.map((t) => (
              <TabsTrigger key={t} value={t} className={TOUCH} aria-controls={VIEW_ID}>
                {t === "approvals" && toDecide > 0
                  ? `${PTO_PAGE_TAB_LABELS[t]} (${toDecide})`
                  : PTO_PAGE_TAB_LABELS[t]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <div id={VIEW_ID} className="flex flex-col gap-5">
        {tab === "loading" ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-11 w-56" />
            <Skeleton className="h-72 w-full" />
          </div>
        ) : tab === "mine" ? (
          <>
            <BalanceCards
              rows={balances.data?.rows}
              isError={balances.isError}
              onRetry={() => void balances.refetch()}
            />
            <MyRequestsTable
              view={requests.data}
              isError={requests.isError}
              onRetry={() => void requests.refetch()}
              onEdit={openDialog}
              onRequest={() => openDialog(null)}
            />
          </>
        ) : (
          <ApprovalsPanel
            isHrApprover={counts.data?.isHrApprover ?? false}
            focus={
              search.decide && search.action ? { id: search.decide, action: search.action } : null
            }
            onFocusDone={() =>
              void navigate({
                to: "/dashboard/pto",
                search: { year: search.year, tab: search.tab },
                replace: true,
              })
            }
          />
        )}
      </div>

      {balances.data && open && (
        <RequestDialog
          open
          onOpenChange={onOpenChange}
          balances={balances.data.rows}
          requests={requests.data}
          editing={editing}
        />
      )}
    </div>
  );
}
